/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import CustomerModel from 'cio/models/customer';
import { MergedAttributes, MergeSummary, ProfileType } from 'cio/utils/people-merge';
import { action } from '@ember/object';
import { IdentifierKey } from 'cio/utils/identifiers';
import { inject as service } from '@ember/service';
import DS from 'ember-data';
import { tracked } from '@glimmer/tracking';
import EnvironmentService from 'cio/services/environment';

interface MergeCustomerProfileArgs {
  customer?: CustomerModel;
  profileType: ProfileType;
  searchTerm: string;
  isLoadingMergedAttributes: boolean;
  mergedAttributes: MergedAttributes;
  mergeSummary?: MergeSummary;
  onSelectAnother: () => unknown;
}

type AttributeItem = {
  key: string;
  value: any;
  object: Record<string, any>;
};

export default class MergeCustomerProfile extends Component<MergeCustomerProfileArgs> {
  @service environment!: EnvironmentService;
  @service store!: DS.Store;

  readonly ProfileType = ProfileType;

  get customer() {
    if (this.args.profileType === ProfileType.Merged) {
      return this.temporaryCustomer;
    }

    return this.args.customer;
  }

  get attributes() {
    if (this.customer == null) {
      return [];
    }

    let attributes = this.customer.filteredAttrsWithIdentifiers.map((attr) => {
      let [key] = Object.keys(attr);

      return {
        key,
        value: attr[key],
        object: attr,
      };
    });

    let unsubscribedExists = Object.keys(this.customer.attrs).includes('unsubscribed');

    // Insert unsubscribed attribute
    if (!unsubscribedExists) {
      let unsubscribedPosition = this.customer.filteredAttrsWithIdentifiers.findIndex((attr) => {
        return this.customer?.isCustomAttribute(Object.keys(attr)[0]);
      });

      // Custom attributes not found
      if (unsubscribedPosition < 0) {
        unsubscribedPosition = attributes.length;
      }

      attributes = [
        ...attributes.slice(0, unsubscribedPosition),
        {
          key: 'unsubscribed',
          value: 'false',
          object: { unsubscribed: 'false' },
        },
        ...attributes.slice(unsubscribedPosition),
      ];
    }

    if (this.args.profileType !== ProfileType.Merged) {
      return attributes;
    }

    let result = attributes.reduce<{ primary: AttributeItem[]; secondary: AttributeItem[] }>(
      (acc, a) => {
        let isFromSecondary = this.mergedAttributeNamesFromOtherProfile.includes(a.key);

        if (isFromSecondary) {
          acc.secondary.push(a);
        } else {
          acc.primary.push(a);
        }

        return acc;
      },
      { primary: [], secondary: [] },
    );

    // If the profile being displayed is the merge preview, we want to show the
    // attributes coming from the secondary profile at the bottom of the
    // attribute list
    return [...result.primary, ...result.secondary];
  }

  get filteredAttributes() {
    return this.attributes.filter((a) => {
      return a.key.includes((this.args.searchTerm ?? '').trim());
    });
  }

  get addedAttributesCount() {
    return this.args.mergeSummary?.attributes.added ?? 0;
  }

  get existingAttributesCount() {
    // We use the attributes length from the customer record rather than the
    // preview summary.attributes[customer.id] count, because the preview
    // doesn't count identifiers that are currently set to empty
    return this.attributes.length - this.addedAttributesCount;
  }

  get mergedAttributeNamesFromOtherProfile() {
    return this.args.mergedAttributes
      .filter((a) => a[IdentifierKey.CioId] !== this.customer?.identifiers[IdentifierKey.CioId])
      .map((a) => a.name);
  }

  // For workspaces with multiple identifiers, it's possible that one of them is
  // null. For example, id could be null in profiles when email is a valid
  // identifier.
  // We still, however, show an empty id in the attributes list.
  // This points us to such identifier attributes, in which case we don't want
  // to show green checkmarks (we show red duplicate icons instead).
  get attributesNotPresentInMerge() {
    let allMergedAttributes = this.args.mergedAttributes.map((a) => a.name);
    return this.attributes.filter((a) => !allMergedAttributes.includes(a.key)).map((a) => a.key);
  }

  @action
  scrollToAttributesAddedFromSecondary() {
    let element = document.querySelector('.js-attribute-merged-from-secondary') as HTMLElement;
    element.scrollIntoView();
  }

  @tracked temporaryCustomer: CustomerModel | null = null;

  @action
  onInsert() {
    if (this.args.profileType === ProfileType.Merged) {
      this.temporaryCustomer = this.store.createRecord('customer', {
        attrs: this.args.mergedAttributes.reduce<Record<string, any>>((acc, a) => {
          acc[a.name] = a.value;
          return acc;
        }, {}),
        identifiers: this.args.customer?.identifiers,
        temporaryMergeProfile: true,
      });
    }
  }

  willDestroy() {
    super.willDestroy();

    if (this.temporaryCustomer != null) {
      this.temporaryCustomer.unloadRecord();
      this.temporaryCustomer = null;
    }
  }
}
